/**
 * DLE encoding/decoding for Protocol B
 *
 * Reserved bytes that must be encoded:
 * STX, ETX, EOT, ENQ, DLE, XON, XOFF, ETB, EOB, EOF, ESC, CR
 * And test bytes: TOK, TML, TRT
 *
 * Encoding: Insert DLE (0x10) before the byte and add 0x40 to the byte
 * Decoding: Remove DLE and subtract 0x40 from the following byte
 */

import { ControlCodes } from '../types/protocol';
import { calculateTransmitCRC } from './crc16';

// Bytes that need DLE encoding
const RESERVED_BYTES = new Set([
  ControlCodes.TOK,   // 0x01
  ControlCodes.STX,   // 0x02
  ControlCodes.ETX,   // 0x03
  ControlCodes.EOT,   // 0x04
  0x05,               // ENQ
  ControlCodes.TML,   // 0x06
  ControlCodes.TRT,   // 0x07
  ControlCodes.CR,    // 0x0D
  ControlCodes.DLE,   // 0x10
  ControlCodes.XON,   // 0x11
  ControlCodes.XOFF,  // 0x13
  ControlCodes.NAK,   // 0x15
  ControlCodes.ETB,   // 0x17
  ControlCodes.EOB,   // 0x18
  ControlCodes.EOF,   // 0x1A
  ControlCodes.ESC,   // 0x1B
]);

/**
 * Check if a byte needs DLE encoding
 */
export function needsEncoding(byte: number): boolean {
  return RESERVED_BYTES.has(byte);
}

/**
 * Encode data bytes for transmission (DLE stuffing)
 * @param data - Raw data bytes
 * @returns Encoded bytes with DLE stuffing
 */
export function encodeData(data: Uint8Array): Uint8Array {
  // First pass: count how many bytes need encoding
  let extraBytes = 0;
  for (let i = 0; i < data.length; i++) {
    if (needsEncoding(data[i])) {
      extraBytes++;
    }
  }

  // Create output buffer
  const encoded = new Uint8Array(data.length + extraBytes);
  let j = 0;

  // Second pass: encode
  for (let i = 0; i < data.length; i++) {
    if (needsEncoding(data[i])) {
      encoded[j++] = ControlCodes.DLE;
      encoded[j++] = data[i] + 0x40;
    } else {
      encoded[j++] = data[i];
    }
  }

  return encoded;
}

/**
 * Decode received data (remove DLE stuffing)
 * @param encoded - Encoded bytes with DLE stuffing
 * @returns Decoded raw data bytes
 */
export function decodeData(encoded: Uint8Array): Uint8Array {
  // First pass: count actual data bytes
  let dataBytes = 0;
  let i = 0;
  while (i < encoded.length) {
    if (encoded[i] === ControlCodes.DLE) {
      i += 2; // Skip DLE and encoded byte
    } else {
      i++;
    }
    dataBytes++;
  }

  // Create output buffer
  const decoded = new Uint8Array(dataBytes);
  i = 0;
  let j = 0;

  // Second pass: decode
  while (i < encoded.length) {
    if (encoded[i] === ControlCodes.DLE) {
      decoded[j++] = encoded[i + 1] - 0x40;
      i += 2;
    } else {
      decoded[j++] = encoded[i++];
    }
  }

  return decoded;
}

/**
 * Build a complete data frame
 * @param data - Raw data bytes
 * @param crc - Calculated CRC
 * @returns Complete frame: STX + encoded(data + CRC) + EOF
 */
export function buildFrame(data: Uint8Array, crc: number): Uint8Array {
  // Combine data and CRC
  const dataWithCRC = new Uint8Array(data.length + 2);
  dataWithCRC.set(data);
  dataWithCRC[data.length] = (crc >> 8) & 0xFF;
  dataWithCRC[data.length + 1] = crc & 0xFF;

  // Encode
  const encoded = encodeData(dataWithCRC);

  // Build frame
  const frame = new Uint8Array(encoded.length + 2);
  frame[0] = ControlCodes.STX;
  frame.set(encoded, 1);
  frame[frame.length - 1] = ControlCodes.EOF;

  return frame;
}

/**
 * Parse a received frame
 * @param frame - Complete frame including STX and EOF
 * @returns Decoded data and CRC validity, or null if invalid frame
 */
export function parseFrame(frame: Uint8Array): { data: Uint8Array; crcValid: boolean } | null {
  // Validate frame markers
  if (frame.length < 4) return null;
  if (frame[0] !== ControlCodes.STX) return null;
  if (frame[frame.length - 1] !== ControlCodes.EOF) return null;

  // Extract and decode content
  const encodedContent = frame.slice(1, -1);
  const decoded = decodeData(encodedContent);

  if (decoded.length < 3) return null; // At least 1 data byte + 2 CRC bytes

  // Split data and CRC
  const data = decoded.slice(0, -2);
  const receivedCRC = (decoded[decoded.length - 2] << 8) | decoded[decoded.length - 1];

  // Verify CRC
  const calculatedCRC = calculateTransmitCRC(data);
  const crcValid = calculatedCRC === receivedCRC;

  return { data, crcValid };
}
